import { defineComponent, h, ref, watch } from 'vue';
import { Modal, modalProps, ModalProps } from '@/ui/components/Modal';
import { SelectOptions } from '@/ui/types';
import { useTask } from '@/ui/concerns';
import { filter as fetchAllRoles } from '@/services/api/resources/role';
import { filterListToOptions } from '@/ui/helpers';
import { useUserRecord } from '@/ui/pages/user/useUserRecord';
import { create, fromEditable } from '@/services/api/resources/user';
import { fetchUser, getQuota, isOnFreePlan } from '@/ui/auth/auth';
import { t } from '@/ui/i18n/i18n';
import { useUserCreateForm } from '@/ui/pages/user/useUserCreateForm';
import { loader, pageSubtitle, button } from '@/ui/components';
import { UpgradeAd } from '@/ui/components/upgrade/UpgradeAd';
import { EventBus } from '@/ui/EventBus';
import { Events } from '@/ui/events';
import { EVENTS, EventTypes, trackEvent } from '@/services/activityTracker/activityTrackerHelper';
import { Userflow, UserflowTrackEvent } from '@/services/userflow/userflow';

// If a page that has two `createUserModal` components rendered in the
// same page we need to identify where the `Events.userCreated`
// is coming from. This enumeration helps identifying this.
export enum CreateUserModelIdentifier {
  checklistBuilderSchedule= 'checklist-builder-schedule',
  checklistBuilderUserRestriction = 'checklist-builder-user-restriction',
  checklistBuilderUserEditor = 'checklist-builder-user-editor',
  checklistBuilderReportViewer = 'checklist-builder-report-viewer',
  checklistBuilderApprovers = 'checklist-builder-approvers',
  issueBuilderApprovers = 'issue-builder-approvers',
  actionCreator = 'issue-creator',
  entityItemCreator = 'entity-item-creator',
  roleCreator = 'role-creator',
  sensorCreator = 'sensor-creator',
  groupCreator = 'group-creator',
  scheduleCreatorAssignees = 'schedule-assignees-creator',
  scheduleCreatorSupervisors = 'schedule-supervisors-creator',
}

type TrackedPages =
  'Template Builder'
  | 'Create/Edit Action'
  | 'Create/Edit Role'
  | 'Create/Edit Entity item'
  | 'Create/Edit Sensor'
  | 'Create/Edit Group';

interface CreateUserModalProps extends ModalProps {
  identifier?: CreateUserModelIdentifier;
}

export const CreateUserModal = defineComponent<CreateUserModalProps>({
  name: 'CreateUserModal',
  props: [...modalProps, 'identifier'],
  setup(props, ctx) {
    const roleOptions = ref<SelectOptions>([]);

    // get record
    const getRecord = useTask(
      () => fetchAllRoles(),
      roles => {
        roleOptions.value = filterListToOptions(roles);
      },
    );

    // create empty record
    const record = useUserRecord();

    // send record
    const sendRecord = useTask(
      () => create(fromEditable(record.value)),
      async response => {
        const { invite, email } = record.value;
        record.value = useUserRecord().value;
        EventBus.$emit(Events.userCreated, { response, identifier: props.identifier });
        ctx.emit('user:created', response.id);
        // update the quota for the user usage
        await fetchUser()();

        if (invite) {
          trackEvent(EventTypes.inviteUser, {
            page: getPageByIdentifier(props.identifier ?? ''),
            'user-email': email,
            ...EVENTS.INVITE_USER,
          });
          Userflow.trackEvent(UserflowTrackEvent.USER_INVITED);
        }
      },
    );

    // form
    const { formFields, handleSubmit } = useUserCreateForm(record, sendRecord);

    getRecord.run();

    watch(() => props.active, () => {
      record.value = useUserRecord().value;
    });

    const getPageByIdentifier = (identifier: string): TrackedPages | '' => {
      switch (identifier) {
        case CreateUserModelIdentifier.checklistBuilderUserRestriction:
        case CreateUserModelIdentifier.checklistBuilderSchedule:
          return 'Template Builder';
        case CreateUserModelIdentifier.groupCreator:
          return 'Create/Edit Group';
        case CreateUserModelIdentifier.actionCreator:
          return 'Create/Edit Action';
        case CreateUserModelIdentifier.roleCreator:
          return 'Create/Edit Role';
        case CreateUserModelIdentifier.sensorCreator:
          return 'Create/Edit Sensor';
        case CreateUserModelIdentifier.entityItemCreator:
          return 'Create/Edit Entity item';
        default:
          return '';
      }
    };

    return function render() {
      const upgradeMessage = h(UpgradeAd, {
        class: 'upgrade-ad--create-user',
        props: {
          message: t('upgrade-ad.user-create-quota', { quota: getQuota('user').toString() }),
          isVertical: true,
        },
      });

      //@ts-ignore
      return h(Modal, { props: { ...props, width: '550px' }, on: ctx.listeners }, [
        pageSubtitle(t('common.create-user')),
        loader('form', getRecord, [
          isOnFreePlan.value ? upgradeMessage : null,
          ...formFields(roleOptions.value),
        ]),
        h('div', { slot: 'actions' }, [
          button(t('common.cancel'), () => ctx.emit('close'), undefined, { btnClass: 'mr-2', type: 'is-text' }),
          button(t('user.create'), handleSubmit, undefined, { loading: sendRecord.state.value.pending }),
        ]),
      ]);
    };
  },
});
